import { RefreshCcw } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Heading, Text, XStack, YStack } from 'tamagui';

import { WiFiNetworkCard, DisconnectDialog } from './components';
import { DraggableScrollView } from '../../components/common/DraggableScrollView';
import { GradientButton } from '../../components/common/GradientButton';
import { useFirmwareContext } from '../../contexts/FirmwareContext';
import { ROUTE_PATHNAMES } from '../../routes/routeConstants';
import { StyledCard } from '../Settings_General/components/styles';

export const WiFiConnectionSettings: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState<'disconnect' | 'reset'>('disconnect');

  const {
    wifiNetworks,
    refreshWifiNetworks,
    wifiStatus,
    refreshWifiStatus,
    disconnectWifi,
    forgetWifi,
    refreshIotStatus,
  } = useFirmwareContext();

  const handleConnectPress = async (networkName: string) => {
    navigate(ROUTE_PATHNAMES.connectWifi.replace(':networkName', encodeURIComponent(networkName)));
    // After navigation, refresh status, networks, and iot status
    await refreshWifiStatus();
    await refreshWifiNetworks();
    await refreshIotStatus();
  };

  // Always update both network list and connection status after actions
  const handleDisconnect = async () => {
    try {
      await disconnectWifi();
      await refreshWifiNetworks();
      await refreshWifiStatus();
      await refreshIotStatus();
      setShowModal(false);
    } catch (err) {
      console.error('Disconnect failed:', err);
    }
  };

  const handleReset = async () => {
    if (wifiStatus?.ssid) {
      try {
        await disconnectWifi();
        await forgetWifi(wifiStatus.ssid);
        await refreshWifiNetworks();
        await refreshWifiStatus();
        await refreshIotStatus();
        setShowModal(false);
      } catch (err) {
        console.error('Reset failed:', err);
      }
    } else {
      await refreshWifiNetworks();
      await refreshWifiStatus();
      await refreshIotStatus();
      setShowModal(false);
    }
  };

  const handleDisconnectPress = () => {
    setModalAction('disconnect');
    setShowModal(true);
  };

  const handleResetPress = () => {
    setModalAction('reset');
    setShowModal(true);
  };

  return (
    <>
      <YStack padding="$5">
        <StyledCard height="432px">
          <XStack justifyContent="space-between" alignItems="center" height="72px">
            <Heading color="$textLight" size="$5.5" fontWeight="$5">
              {t('settings.wifi.selectNetwork')}
            </Heading>
            <XStack gap="$5" alignItems="center">
              <Button
                borderWidth={2}
                borderColor="rgba(255, 255, 255, 0.05)"
                backgroundColor="transparent"
                borderBlockWidth="$1"
                borderRadius={36}
                width="186px"
                height="72px"
                onPress={handleResetPress}>
                <Text fontSize="$8.5" whiteSpace="nowrap">
                  {t('settings.wifi.resetWifi')}
                </Text>
              </Button>
              <GradientButton
                label={t('settings.wifi.refresh')}
                isActive={true}
                icon={<RefreshCcw strokeWidth={2} />}
                onPress={async () => {
                  await refreshWifiNetworks();
                  await refreshWifiStatus();
                }}
              />
            </XStack>
          </XStack>
          <DraggableScrollView marginLeft={0} paddingVertical={0}>
            <XStack gap="$5">
              {wifiNetworks.map((network, idx) => (
                <WiFiNetworkCard
                  key={network.ssid + idx}
                  name={network.ssid}
                  isConnected={
                    wifiStatus?.status === 'connected' && network.ssid === wifiStatus.ssid
                  }
                  onConnect={() => handleConnectPress(network.ssid)}
                  onDisconnect={handleDisconnectPress}
                />
              ))}
            </XStack>
          </DraggableScrollView>
        </StyledCard>
      </YStack>
      <DisconnectDialog
        open={showModal}
        onOpenChange={setShowModal}
        onCancel={() => setShowModal(false)}
        onDisconnect={modalAction === 'reset' ? handleReset : handleDisconnect}
        modalAction={modalAction}
      />
    </>
  );
};
